import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Download, FileText, Mail, MessageCircle, Pencil, Phone, Save, X, ShieldCheck, ShieldAlert, Clock, Trash2 } from "lucide-react";
import { SupplierDeleteBox } from "./SupplierDelete";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { API, abs } from "./shared";

const dash = (v) => (v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length) ? "—" : Array.isArray(v) ? v.join(", ") : String(v));
const fmtDate = (d) => (d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const digits = (p) => (p || "").replace(/\D/g, "");
const waNumber = (p) => { const d = digits(p); return d.length === 10 ? `91${d}` : d; };
const STATUS_TONE = { Verified: ["#ECFDF5", "#047857", ShieldCheck], Pending: ["#FFFBEB", "#B45309", Clock], Rejected: ["#FEF2F2", "#B91C1C", ShieldAlert] };
const ibox = { width: "100%", border: "1px solid #CBD5E1", borderRadius: 8, padding: "7px 9px", fontSize: 13, fontFamily: "inherit", background: "#fff" };

const FRIENDLY = {
  pincode: "PIN code must be 6 digits", gstin: "GSTIN must be 15 characters, e.g. 27ABCDE1234F1Z5", pan: "PAN must look like ABCDE1234F",
  established_year: "Enter a valid year established", lat: "Latitude must be between -90 and 90", lng: "Longitude must be between -180 and 180",
  email: "Enter a valid email address",
};
const EDIT_KEYS = ["company", "brand_name", "business_type", "established_year", "name", "contact_person", "email", "phone", "whatsapp",
  "address_line1", "address_line2", "city", "state", "pincode", "lat", "lng", "gstin", "pan", "msme_number", "equipment", "daily_capacity", "moq"];

const toForm = (o) => ({
  company: o.business.company, brand_name: o.business.brand_name, business_type: o.business.business_type, established_year: o.business.established_year,
  name: o.contact.name, contact_person: o.contact.contact_person, email: o.contact.email, phone: o.contact.phone, whatsapp: o.contact.whatsapp,
  ...o.address, gstin: o.tax.gstin, pan: o.tax.pan, msme_number: o.tax.msme_number,
  equipment: o.operations.equipment, daily_capacity: o.operations.daily_capacity, moq: o.operations.moq,
});

function Field({ label, value, k, edit, form, setForm, type = "text", options, area, testid }) {
  return (
    <div data-testid={testid || `so-field-${k || label.toLowerCase().replace(/\W+/g, "-")}`} style={{ padding: "12px 0", borderBottom: "1px solid #F1F5F9" }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: ".06em", textTransform: "uppercase", color: "#64748b", marginBottom: 4 }}>{label}</div>
      {edit && k ? (
        options ? (
          <select data-testid={`so-input-${k}`} value={form[k] || ""} onChange={e => setForm({ ...form, [k]: e.target.value })} style={ibox}>
            <option value="">—</option>{options.map(o => <option key={o}>{o}</option>)}
          </select>
        ) : area ? (
          <textarea data-testid={`so-input-${k}`} rows={3} value={form[k] ?? ""} onChange={e => setForm({ ...form, [k]: e.target.value })} style={ibox}/>
        ) : (
          <input data-testid={`so-input-${k}`} type={type} value={form[k] ?? ""} onChange={e => setForm({ ...form, [k]: e.target.value })} style={ibox}/>
        )
      ) : <div style={{ fontSize: 13.5, color: "#0F172A", wordBreak: "break-word" }}>{value === undefined ? "—" : value}</div>}
    </div>
  );
}

const Grid = ({ children }) => <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", columnGap: 32 }}>{children}</div>;

function DocRow({ d, k }) {
  const open = async () => {
    try {
      const r = await axios.get(abs(d.url), { responseType: "blob" });
      window.open(URL.createObjectURL(r.data), "_blank");
    } catch { toast.error("Could not open document"); }
  };
  return (
    <div data-testid={`so-doc-${k}`} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "11px 14px", border: "1px solid #E2E8F0", borderRadius: 10, marginBottom: 8 }}>
      <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
        <FileText size={16} style={{ color: d.file_id ? "#1D4ED8" : "#94a3b8" }}/>
        <div><b style={{ fontSize: 13 }}>{d.label}</b><div style={{ fontSize: 11.5, color: "#64748b" }}>{d.file_id ? `${d.name || "file"} · ${fmtDate(d.uploaded_at)}` : "Not provided"}</div></div>
      </div>
      {d.file_id && <button data-testid={`so-doc-view-${k}`} onClick={open} className="so-btn">View</button>}
    </div>
  );
}

function Verification({ o, onChange }) {
  const [note, setNote] = useState("");
  const [tone, fg, Icon] = STATUS_TONE[o.tax.status] || STATUS_TONE.Pending;
  const set = async (status) => {
    try { onChange((await axios.post(`${API}/admin/suppliers/${o.id}/verification`, { status, note: note || undefined })).data); setNote(""); toast.success(`Marked ${status}`); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update verification"); }
  };
  return (
    <div style={{ background: "#F8FAFC", border: "1px solid #E2E8F0", borderRadius: 12, padding: 16, marginBottom: 18 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
        <span data-testid="so-verification-status" style={{ display: "inline-flex", alignItems: "center", gap: 5, background: tone, color: fg, fontWeight: 700, fontSize: 12, padding: "4px 10px", borderRadius: 999 }}><Icon size={13}/>{o.tax.status}</span>
        {o.tax.stage && <span style={{ fontSize: 12, color: "#64748b" }}>Stage: {o.tax.stage}</span>}
        {o.tax.verified_at && <span style={{ fontSize: 12, color: "#64748b" }}>· Verified {fmtDate(o.tax.verified_at)}</span>}
      </div>
      {o.tax.kyc_note && <div style={{ fontSize: 12.5, color: "#475569", marginBottom: 10 }}>Note: {o.tax.kyc_note}</div>}
      <input data-testid="so-verification-note" value={note} onChange={e => setNote(e.target.value)} placeholder="Optional note (shown to supplier on rejection)" style={{ ...ibox, marginBottom: 10 }}/>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button data-testid="so-verify-btn" className="so-btn so-green" onClick={() => set("verified")}>Mark verified</button>
        <button data-testid="so-pending-btn" className="so-btn" onClick={() => set("pending")}>Back to pending</button>
        <button data-testid="so-reject-btn" className="so-btn so-red" onClick={() => set("rejected")}>Reject</button>
      </div>
    </div>
  );
}

function MessageBox({ o, close }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try { await axios.post(`${API}/admin/suppliers/${o.id}/message`, { text }); toast.success("Notification sent"); close(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not send"); } finally { setBusy(false); }
  };
  return (
    <div data-testid="so-message-box" style={{ border: "1px solid #BFDBFE", background: "#EFF6FF", borderRadius: 12, padding: 14, margin: "0 0 16px" }}>
      <b style={{ fontSize: 13 }}>In-app notification to {o.business.company || o.contact.name}</b>
      <textarea data-testid="so-message-text" rows={3} value={text} onChange={e => setText(e.target.value)} placeholder="Type your message…" style={{ ...ibox, margin: "8px 0" }}/>
      <div style={{ display: "flex", gap: 8 }}>
        <button data-testid="so-message-send" className="so-btn so-blue" disabled={busy || text.trim().length < 2} onClick={send}>{busy ? "Sending…" : "Send"}</button>
        <button data-testid="so-message-cancel" className="so-btn" onClick={close}>Cancel</button>
      </div>
    </div>
  );
}

function Header({ o, edit, saving, onEdit, onSave, onCancel, onMsg, onExport, onDelete }) {
  const phone = digits(o.contact.phone);
  const wa = waNumber(o.contact.whatsapp || o.contact.phone);
  const Btn = ({ id, icon: I, label, href, onClick, disabled, cls = "" }) => href && !disabled
    ? <a data-testid={id} href={href} target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer" className={`so-btn ${cls}`}><I size={14}/>{label}</a>
    : <button data-testid={id} onClick={onClick} disabled={disabled} className={`so-btn ${cls}`} title={disabled ? "Not available" : undefined}><I size={14}/>{label}</button>;
  return (
    <div style={{ padding: "20px 28px 16px", borderBottom: "1px solid #E2E8F0", background: "#fff" }}>
      <p className="eyebrow" style={{ margin: 0 }}>{o.role_label} · ID {o.id}</p>
      <DialogTitle data-testid="so-title" style={{ fontSize: 24, fontWeight: 800, margin: "4px 0 2px", color: "#0F172A" }}>
        {o.business.company || o.contact.name}
        {o.account.is_test_account && <span style={{ fontSize: 10, fontWeight: 800, padding: "2px 6px", borderRadius: 4, background: "#FEF3C7", color: "#92400E", marginLeft: 8, verticalAlign: "middle" }}>TEST</span>}
      </DialogTitle>
      <DialogDescription style={{ fontSize: 12.5, color: "#64748b" }}>{[o.contact.email, o.address.city, o.address.state].filter(Boolean).join(" · ")}</DialogDescription>
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 14 }}>
        {edit ? (<>
          <Btn id="so-save-btn" icon={Save} label={saving ? "Saving…" : "Save changes"} onClick={onSave} disabled={saving} cls="so-blue"/>
          <Btn id="so-cancel-edit-btn" icon={X} label="Cancel" onClick={onCancel}/>
        </>) : <Btn id="so-edit-btn" icon={Pencil} label="Edit profile" onClick={onEdit} cls="so-blue"/>}
        <span style={{ width: 1, background: "#E2E8F0", margin: "0 4px" }}/>
        <Btn id="so-email-btn" icon={Mail} label="Email" href={o.contact.email ? `mailto:${o.contact.email}` : null} disabled={!o.contact.email}/>
        <Btn id="so-call-btn" icon={Phone} label="Call" href={phone ? `tel:+${waNumber(phone)}` : null} disabled={!phone}/>
        <Btn id="so-whatsapp-btn" icon={MessageCircle} label="WhatsApp" href={wa ? `https://wa.me/${wa}` : null} disabled={!wa}/>
        <Btn id="so-notify-btn" icon={Bell} label="Notify" onClick={onMsg}/>
        <span style={{ width: 1, background: "#E2E8F0", margin: "0 4px" }}/>
        <Btn id="so-export-pdf-btn" icon={Download} label="PDF" onClick={() => onExport("pdf")}/>
        <Btn id="so-export-csv-btn" icon={Download} label="CSV" onClick={() => onExport("csv")}/>
        <span style={{ width: 1, background: "#E2E8F0", margin: "0 4px" }}/>
        <Btn id="so-delete-btn" icon={Trash2} label="Delete account" onClick={onDelete} cls="so-red"/>
      </div>
    </div>
  );
}

function Body({ o, edit, form, setForm, setO }) {
  const f = { edit, form, setForm };
  const op = o.operations, ac = o.account;
  return (
    <Tabs defaultValue="business" style={{ padding: "0 28px 28px", maxWidth: 1200 }}>
      <TabsList data-testid="so-tabs" style={{ margin: "16px 0 8px" }}>
        {[["business", "Business"], ["contact", "Contact & Address"], ["tax", "Tax Docs"], ["capacity", "Capacity"], ["plan", "Plan"]].map(([v, l]) =>
          <TabsTrigger key={v} value={v} data-testid={`so-tab-${v}`}>{l}</TabsTrigger>)}
      </TabsList>
      <TabsContent value="business" data-testid="so-panel-business"><Grid>
        <Field label="Company name" k="company" value={dash(o.business.company)} {...f}/>
        <Field label="Brand / trade name" k="brand_name" value={dash(o.business.brand_name)} {...f}/>
        <Field label="Business type" k="business_type" value={dash(o.business.business_type)} options={o.business_types} {...f}/>
        <Field label="Year established" k="established_year" type="number" value={dash(o.business.established_year)} {...f}/>
        <Field label="Supplier type" value={o.role_label}/>
        <Field label="Team size" value={dash(o.business.team_size)}/>
        <Field label="About" value={dash(o.business.about)}/>
      </Grid></TabsContent>
      <TabsContent value="contact" data-testid="so-panel-contact"><Grid>
        <Field label="Account name" k="name" value={dash(o.contact.name)} {...f}/>
        <Field label="Contact person" k="contact_person" value={dash(o.contact.contact_person)} {...f}/>
        <Field label="Email" k="email" type="email" value={dash(o.contact.email)} {...f}/>
        <Field label="Phone" k="phone" value={o.contact.phone ? `${o.contact.phone} ${o.contact.phone_verified ? "✓ verified" : "· not verified"}` : "—"} {...f}/>
        <Field label="WhatsApp" k="whatsapp" value={dash(o.contact.whatsapp)} {...f}/>
        <Field label="Address line 1" k="address_line1" value={dash(o.address.address_line1)} {...f}/>
        <Field label="Address line 2" k="address_line2" value={dash(o.address.address_line2)} {...f}/>
        <Field label="City" k="city" value={dash(o.address.city)} {...f}/>
        <Field label="State" k="state" value={dash(o.address.state)} {...f}/>
        <Field label="PIN code" k="pincode" value={dash(o.address.pincode)} {...f}/>
        <Field label="Latitude" k="lat" type="number" value={dash(o.address.lat)} {...f}/>
        <Field label="Longitude" k="lng" type="number" value={dash(o.address.lng)} {...f}/>
      </Grid>
        {o.address.lat != null && o.address.lng != null && !edit &&
          <a data-testid="so-map-link" className="so-btn" style={{ marginTop: 12 }} target="_blank" rel="noreferrer" href={`https://www.google.com/maps?q=${o.address.lat},${o.address.lng}`}>Open in Google Maps</a>}
      </TabsContent>
      <TabsContent value="tax" data-testid="so-panel-tax">
        <Verification o={o} onChange={setO}/>
        <Grid>
          <Field label="GSTIN" k="gstin" value={dash(o.tax.gstin)} {...f}/>
          <Field label="PAN" k="pan" value={dash(o.tax.pan)} {...f}/>
          <Field label="MSME / Udyam number" k="msme_number" value={dash(o.tax.msme_number)} {...f}/>
        </Grid>
        <h4 style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: ".06em", color: "#64748b", margin: "20px 0 10px" }}>Uploaded documents</h4>
        {Object.entries(o.tax.documents).map(([k, d]) => <DocRow key={k} k={k} d={d}/>)}
      </TabsContent>
      <TabsContent value="capacity" data-testid="so-panel-capacity"><Grid>
        <Field label="Equipment / machinery" k="equipment" area value={dash(op.equipment)} {...f}/>
        <Field label="Daily capacity" k="daily_capacity" value={dash(op.daily_capacity)} {...f}/>
        <Field label="Minimum order quantity" k="moq" value={dash(op.moq)} {...f}/>
        <Field label="Substrates" value={dash(op.substrates)}/>
        <Field label="Post-press" value={dash(op.post_press)}/>
        <Field label="Products" value={dash(op.products)}/>
        <Field label="Categories" value={dash(op.categories)}/>
        <Field label="Specialities" value={dash(op.specialities)}/>
        <Field label="Service radius" value={op.service_radius_km ? `${op.service_radius_km} km` : "—"}/>
        <Field label="Typical turnaround" value={op.turnaround_days ? `${op.turnaround_days} days` : "—"}/>
      </Grid></TabsContent>
      <TabsContent value="plan" data-testid="so-panel-plan"><Grid>
        <Field label="Plan" testid="so-plan-tier" value={dash(ac.plan && ac.plan.replace(/_/g, " "))}/>
        <Field label="Billing cycle" value={dash(ac.billing_cycle)}/>
        <Field label="Plan expires" value={fmtDate(ac.plan_expires_at)}/>
        <Field label="Account status" testid="so-account-status" value={ac.status + (ac.suspended_reason ? ` — ${ac.suspended_reason}` : "")}/>
        <Field label="Joined" value={fmtDate(ac.joined_at)}/>
        <Field label="Jobs completed" value={dash(ac.jobs_completed)}/>
        <Field label="Quality rating" value={dash(ac.rating)}/>
        <Field label="Payout bank" value={ac.payout_bank ? `${ac.payout_bank}${ac.payout_account_last4 ? ` ••••${ac.payout_account_last4}` : ""}` : "—"}/>
        <Field label="Profile last updated" value={fmtDate(ac.profile_updated_at)}/>
      </Grid></TabsContent>
    </Tabs>
  );
}

const clean = (form, o) => {
  const base = toForm(o), out = {};
  EDIT_KEYS.forEach(k => {
    let v = form[k];
    if (typeof v === "string") v = v.trim();
    if (v === "" ) v = null;
    if (["established_year"].includes(k) && v != null) v = parseInt(v, 10);
    if (["lat", "lng"].includes(k) && v != null) v = parseFloat(v);
    if (["gstin", "pan"].includes(k) && v) v = v.toUpperCase();
    if ((v ?? null) !== (base[k] ?? null)) out[k] = v;
  });
  return out;
};

export function SupplierOverview({ uid, onClose, onSaved }) {
  const [o, setO] = useState(null);
  const [edit, setEdit] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(false);
  const [del, setDel] = useState(false);
  useEffect(() => {
    setO(null); setEdit(false); setMsg(false);
    if (uid) axios.get(`${API}/admin/suppliers/${uid}/profile`).then(r => setO(r.data)).catch(e => { toast.error(e.response?.data?.detail || "Could not load supplier"); onClose(); });
  }, [uid]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    const body = clean(form, o);
    if (!Object.keys(body).length) { setEdit(false); return; }
    setSaving(true);
    try { setO((await axios.patch(`${API}/admin/suppliers/${uid}/profile`, body)).data); setEdit(false); toast.success("Profile updated"); onSaved?.(); }
    catch (e) { const d = e.response?.data?.detail; toast.error(Array.isArray(d) ? (FRIENDLY[d[0]?.loc?.slice(-1)[0]] || `Please check ${d[0]?.loc?.slice(-1)[0]}`) : d || "Could not save"); }
    finally { setSaving(false); }
  };
  const exportFile = async (kind) => {
    try {
      const r = await axios.get(`${API}/admin/suppliers/${uid}/export.${kind}`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(r.data);
      a.download = `supplier-${(o.business.company || uid).replace(/\W+/g, "-").toLowerCase()}.${kind}`;
      a.click();
      toast.success(`${kind.toUpperCase()} downloaded`);
    } catch { toast.error("Export failed"); }
  };
  return (
    <Dialog open={!!uid} onOpenChange={v => !v && onClose()}>
      <DialogContent data-testid="supplier-overview" className="so-full" style={{ maxWidth: "none", width: "100vw", height: "100vh", padding: 0, borderRadius: 0, overflowY: "auto", display: "block", background: "#FAFBFD", zIndex: 1200 }}>
        {!o ? <div data-testid="so-loading" style={{ padding: 40, color: "#64748b" }}><DialogTitle style={{ fontSize: 14 }}>Loading supplier…</DialogTitle></div> : (<>
          <Header o={o} edit={edit} saving={saving} onEdit={() => { setForm(toForm(o)); setEdit(true); }} onSave={save} onCancel={() => setEdit(false)}
            onMsg={() => setMsg(true)} onExport={exportFile} onDelete={() => setDel(true)}/>
          {del && <div style={{ padding: "16px 28px 0" }}><SupplierDeleteBox uid={uid} close={() => setDel(false)} onDeleted={() => { onSaved?.(); onClose(); }}/></div>}
          {msg && <div style={{ padding: "16px 28px 0" }}><MessageBox o={o} close={() => setMsg(false)}/></div>}
          <Body o={o} edit={edit} form={form} setForm={setForm} setO={(n) => { setO(n); onSaved?.(); }}/>
        </>)}
      </DialogContent>
    </Dialog>
  );
}
